import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { requestCreateSourceDef, requestUpdateSourceDef } from "./msgs/MsgSender";
import {
    convertSourceDefToSourceDefCreator,
    SamSourceDef,
    SamSourceDefCreator,
} from "userful-chronos-app-common-js/dist/models/sam/SAMSource";
import { initSamSourceDefCreator } from "userful-chronos-app-common-js/dist/models/sam/SAMUtils";
import { SamUsageDataResponse } from "userful-chronos-app-common-js/dist/models/sam/Common";
import { ConfirmationToastContentStore } from "../../Container/ConfirmationToastContentStore";
import { toast } from "react-toastify";
import { APP_IDS, StringID } from "userful-chronos-app-common-js/dist/models/common";
import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";

const initialState: {
    currentSource: SamSourceDefCreator;
    compGroups?: CompGroup[];
    parentGroupId?: StringID;
    updatedSourceDef: SamSourceDef;
    isCreate: boolean;
    isDuplicate: boolean;
    expectedConfirmation: string;
    requireForceUpdate: SamUsageDataResponse;
} = {
    currentSource: null,
    compGroups: undefined,
    parentGroupId: undefined,
    updatedSourceDef: null,
    isCreate: false,
    isDuplicate: false,
    expectedConfirmation: null,
    requireForceUpdate: null,
};

export const sourceEditSlice = createSlice({
    name: "sourceEditSlice",
    initialState,
    reducers: {
        onCreate: (state, action: PayloadAction<{ appID: string }>) => {
            state.currentSource = initSamSourceDefCreator(
                action.payload.appID !== APP_IDS.Config && action.payload.appID,
            );
            state.updatedSourceDef = null;
            state.compGroups = undefined;
            state.parentGroupId = undefined;
            state.isCreate = true;
            state.isDuplicate = false;
        },
        onEdit: (
            state,
            action: PayloadAction<{ source: SamSourceDef; compGroups?: CompGroup[]; parentGroupId?: StringID }>,
        ) => {
            state.currentSource = convertSourceDefToSourceDefCreator(action.payload.source);
            state.updatedSourceDef = null;
            state.compGroups = action.payload.compGroups;
            state.parentGroupId = action.payload.parentGroupId;
            state.isCreate = false;
            state.isDuplicate = false;
        },
        onDuplicate: (
            state,
            action: PayloadAction<{ source: SamSourceDef; compGroups?: CompGroup[]; parentGroupId?: StringID }>,
        ) => {
            state.currentSource = convertSourceDefToSourceDefCreator(action.payload.source, true);
            state.updatedSourceDef = null;
            state.compGroups = action.payload.compGroups;
            state.parentGroupId = action.payload.parentGroupId;
            state.isCreate = false;
            state.isDuplicate = true;
        },
        onCancel: (state) => {
            state.currentSource = null;
            state.updatedSourceDef = null;
            state.compGroups = undefined;
            state.parentGroupId = undefined;
            state.isCreate = false;
            state.isDuplicate = false;
            state.requireForceUpdate = null;
        },
        updateEditSource: (state, action: PayloadAction<SamSourceDefCreator>) => {
            state.currentSource = { ...action.payload };
        },
        addOrUpdateSourceToServer: (state, action: PayloadAction<SamSourceDefCreator>) => {
            state.expectedConfirmation = state.currentSource.id.value;
            state.updatedSourceDef = null;
            if (state.isCreate || state.isDuplicate) {
                requestCreateSourceDef(action.payload);
                state.currentSource = null;
                state.isCreate = false;
                state.isDuplicate = false;
                state.requireForceUpdate = null;
            } else {
                state.currentSource = action.payload;
                requestUpdateSourceDef(action.payload, false);
            }
        },
        forceUpdateSourceToServer: (state) => {
            state.requireForceUpdate = null;
            state.expectedConfirmation = state.currentSource.id.value;
            state.updatedSourceDef = null;
            requestUpdateSourceDef(state.currentSource, true);
        },
        sourceAdded: (state, action: PayloadAction<SamSourceDef>) => {
            if (state.expectedConfirmation === action.payload.id.value) {
                const customDisplay = ConfirmationToastContentStore(action.payload.name, "Save");
                toast(customDisplay, { containerId: "confirmationContainer" });
                state.expectedConfirmation = null;
            }
        },
        sourceUpdated: (state, action: PayloadAction<SamSourceDef>) => {
            state.updatedSourceDef = { ...action.payload };
            if (state.expectedConfirmation === action.payload.id.value) {
                const customDisplay = ConfirmationToastContentStore(action.payload.name, "Edit");
                toast(customDisplay, { containerId: "confirmationContainer" });
                state.expectedConfirmation = null;
            }
        },
        receiveUpdateSourceResponse: (state, action: PayloadAction<SamUsageDataResponse>) => {
            if (!state.isCreate && state.currentSource && state.currentSource.id.value === action.payload.itemID) {
                if (action.payload.usageData.length > 0) {
                    state.requireForceUpdate = { ...action.payload };
                } else {
                    state.currentSource = null;
                    state.requireForceUpdate = null;
                }
            }
        },
        clearForceUpdateConfirm: (state) => {
            state.requireForceUpdate = null;
        },
    },
});

export const sourceEditSliceActions = sourceEditSlice.actions;

export default sourceEditSlice.reducer;
